<script setup lang="ts">
import {
  CalendarDays,
  ChevronDown,
  ChevronUp,
  Languages,
  MapPin,
  Wrench,
} from "lucide-vue-next";
import type { PublicWorkerProfile } from "~/types/public-worker";
import { toTitleCase } from "~/utils/search-catalog";

const props = defineProps<{
  worker: PublicWorkerProfile;
}>();

const { locale, t } = useI18n();

const showFullOverview = ref(false);
const showAllLanguages = ref(false);
const showAllRegions = ref(false);

const overviewElement = ref<HTMLElement | null>(null);
const overviewIsLong = ref(false);

let overviewResizeObserver: ResizeObserver | null = null;

const fullName = computed(() =>
  [props.worker.name, props.worker.lastname]
    .filter(Boolean)
    .join(" ")
    .trim()
    .replace(/(^|[\s'-])\p{L}/gu, value => value.toUpperCase()),
);

const categoryName = computed(() =>
  props.worker.primaryCategoryName
    ? catalogLabel("category", props.worker.primaryCategoryName)
    : "",
);

const overview = computed(
  () => props.worker.overview?.trim() || t("worker_profile.no_about"),
);

const visibleLanguages = computed(() =>
  showAllLanguages.value
    ? props.worker.languages
    : props.worker.languages.slice(0, 4),
);

const serviceAreaNames = computed(() =>
  props.worker.coverageType === "nationwide"
    ? [t("worker_profile.nationwide")]
    : props.worker.regions,
);

const visibleRegions = computed(() =>
  showAllRegions.value
    ? serviceAreaNames.value
    : serviceAreaNames.value.slice(0, 5),
);

function catalogLabel(type: "category" | "region" | "language", value: string) {
  const key = `${type}.${value}`;
  const translated = t(key);

  if (translated !== key) {
    return translated;
  }

  return type === "language" ? value.toUpperCase() : toTitleCase(value);
}

async function checkOverviewOverflow() {
  if (showFullOverview.value) return;

  await nextTick();

  const element = overviewElement.value;

  if (!element) {
    overviewIsLong.value = false;
    return;
  }

  overviewIsLong.value = element.scrollHeight > element.clientHeight + 1;
}

async function toggleOverview() {
  showFullOverview.value = !showFullOverview.value;

  if (!showFullOverview.value) {
    await checkOverviewOverflow();
  }
}

watch(overview, async () => {
  showFullOverview.value = false;
  overviewIsLong.value = false;

  await checkOverviewOverflow();
});

onMounted(async () => {
  await checkOverviewOverflow();

  if (!overviewElement.value) return;

  overviewResizeObserver = new ResizeObserver(() => {
    if (!showFullOverview.value) {
      checkOverviewOverflow();
    }
  });

  overviewResizeObserver.observe(overviewElement.value);
});

onBeforeUnmount(() => {
  overviewResizeObserver?.disconnect();
  overviewResizeObserver = null;
});
</script>

<template>
  <section
    id="about"
    class="scroll-mt-36 border-b border-app-border py-10 sm:py-12"
  >
    <WorkerPublicSectionHeading
      :title="$t('worker_profile.about_professional', { name: fullName })"
      :description="$t('worker_profile.about_professional_description')"
    />

    <div class="mt-7 max-w-[820px]">
      <p
        ref="overviewElement"
        class="whitespace-pre-wrap break-words text-base leading-7 text-app-text/85"
        :class="showFullOverview ? '' : 'line-clamp-4'"
      >
        {{ overview }}
      </p>

      <button
        v-if="overviewIsLong"
        type="button"
        class="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition hover:opacity-75"
        :aria-expanded="showFullOverview"
        @click="toggleOverview"
      >
        {{
          showFullOverview
            ? $t("worker_profile.show_less")
            : $t("worker_profile.show_more")
        }}

        <ChevronUp v-if="showFullOverview" class="size-4" />

        <ChevronDown v-else class="size-4" />
      </button>
    </div>

    <div
      v-if="
        categoryName ||
        worker.languages.length ||
        serviceAreaNames.length ||
        worker.createdAt
      "
      id="professional-details"
      class="mt-9 scroll-mt-36"
    >
      <h3 class="text-xl font-bold tracking-tight text-app-text">
        {{ $t("worker_profile.professional_details") }}
      </h3>

      <dl class="mt-4 divide-y divide-app-border">
        <div
          v-if="categoryName"
          class="grid min-w-0 grid-cols-[40px_minmax(0,1fr)] items-center gap-x-3 gap-y-1 py-4 first:pt-0 last:pb-0 sm:grid-cols-[40px_190px_minmax(0,1fr)]"
        >
          <span
            class="row-span-2 flex size-9 items-center justify-center rounded-lg bg-primary-50 text-primary sm:row-span-1"
          >
            <Wrench class="size-4" />
          </span>

          <dt
            class="flex min-h-9 items-center text-sm font-medium text-app-muted"
          >
            {{ $t("worker_profile.stat_main_specialisation") }}
          </dt>

          <dd
            class="col-start-2 flex min-h-9 items-center break-words text-sm font-semibold leading-6 text-app-text sm:col-start-3"
          >
            {{ categoryName }}
          </dd>
        </div>

        <div
          v-if="worker.languages.length"
          class="grid min-w-0 grid-cols-[40px_minmax(0,1fr)] items-center gap-x-3 gap-y-1 py-4 first:pt-0 last:pb-0 sm:grid-cols-[40px_190px_minmax(0,1fr)]"
        >
          <span
            class="row-span-2 flex size-9 items-center justify-center rounded-lg bg-primary-50 text-primary sm:row-span-1"
          >
            <Languages class="size-4" />
          </span>

          <dt
            class="flex min-h-9 items-center text-sm font-medium text-app-muted"
          >
            {{ $t("worker_profile.languages") }}
          </dt>

          <dd
            class="col-start-2 flex min-h-9 flex-wrap items-center break-words text-sm font-semibold leading-6 text-app-text sm:col-start-3"
          >
            <span>
              {{
                visibleLanguages
                  .map(value => catalogLabel("language", value))
                  .join(", ")
              }}
            </span>

            <button
              v-if="worker.languages.length > 4"
              type="button"
              class="ml-1 text-primary animated-underline"
              @click="showAllLanguages = !showAllLanguages"
            >
              {{
                showAllLanguages
                  ? $t("worker_profile.show_less")
                  : $t("worker_profile.show_more_count", {
                      count: worker.languages.length - 4,
                    })
              }}
            </button>
          </dd>
        </div>

        <div
          v-if="serviceAreaNames.length"
          class="grid min-w-0 grid-cols-[40px_minmax(0,1fr)] items-center gap-x-3 gap-y-1 py-4 first:pt-0 last:pb-0 sm:grid-cols-[40px_190px_minmax(0,1fr)]"
        >
          <span
            class="row-span-2 flex size-9 items-center justify-center rounded-lg bg-primary-50 text-primary sm:row-span-1"
          >
            <MapPin class="size-4" />
          </span>

          <dt
            class="flex min-h-9 items-center text-sm font-medium text-app-muted"
          >
            {{ $t("worker_profile.service_areas") }}
          </dt>

          <dd
            class="col-start-2 flex min-h-9 flex-wrap items-center break-words text-sm font-semibold leading-6 text-app-text sm:col-start-3"
          >
            <span>
              {{
                worker.coverageType === "nationwide"
                  ? visibleRegions.join(", ")
                  : visibleRegions
                      .map(value => catalogLabel("region", value))
                      .join(", ")
              }}
            </span>

            <button
              v-if="serviceAreaNames.length > 5"
              type="button"
              class="ml-1 text-primary animated-underline"
              @click="showAllRegions = !showAllRegions"
            >
              {{
                showAllRegions
                  ? $t("worker_profile.show_less")
                  : $t("worker_profile.show_more_count", {
                      count: serviceAreaNames.length - 5,
                    })
              }}
            </button>
          </dd>
        </div>

        <div
          v-if="worker.createdAt"
          class="grid min-w-0 grid-cols-[40px_minmax(0,1fr)] items-center gap-x-3 gap-y-1 py-4 first:pt-0 last:pb-0 sm:grid-cols-[40px_190px_minmax(0,1fr)]"
        >
          <span
            class="row-span-2 flex size-9 items-center justify-center rounded-lg bg-primary-50 text-primary sm:row-span-1"
          >
            <CalendarDays class="size-4" />
          </span>

          <dt
            class="flex min-h-9 items-center text-sm font-medium text-app-muted"
          >
            {{ $t("worker_profile.member_since") }}
          </dt>

          <dd
            class="col-start-2 flex min-h-9 items-center break-words text-sm font-semibold leading-6 text-app-text sm:col-start-3"
          >
            {{
              new Intl.DateTimeFormat(locale, {
                month: "long",
                year: "numeric",
              }).format(new Date(worker.createdAt))
            }}
          </dd>
        </div>
      </dl>
    </div>
  </section>
</template>
